import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Chip, Popover } from '@c2fo/liquidity';
import invoices from '@/enterprise/assets/invoicesgrey.svg?url';
import NoDataState from '@/enterprise/components/NoDataState';
import {
  Table,
  TableBody,
  TableHead,
  TableHeader,
  TableRow,
  TableCell,
  TableDisplay,
  TableSortArrow,
} from '@/enterprise/components/Table';
import useClearingInvoices from '@/enterprise/data/useClearingInvoices';
import useLocaleFormat from '@/utils/useLocaleFormat';
import useUserHasIndiaDivisions from '@/utils/useUserHasIndiaDivisions';
import { AwardedInvoiceSortKey, AwardedInvoiceSource, AwardedInvoiceUrlState } from './types';
import { buildAwardedSearch, mapClearingInvoicesToAwardedRows, statusChipConfig } from './utils';

export const AwardedInvoicesTable = ({
  currentCursor,
  setNextCursor,
  urlState,
  handleSort,
  source,
}: {
  currentCursor: string | null;
  setNextCursor: (cursor: string | null) => void;
  urlState: AwardedInvoiceUrlState;
  handleSort: (sortKey: AwardedInvoiceSortKey) => void;
  source: AwardedInvoiceSource;
}) => {
  const { t } = useTranslation();
  const { asDateStringText, asCurrency, asPercent } = useLocaleFormat();
  const hasIndiaDivisions = useUserHasIndiaDivisions();
  const { makerOrganizationUuid, takerMarkets } = source;

  const search = useMemo(() => buildAwardedSearch(takerMarkets), [takerMarkets]);
  const { sort } = urlState;

  const { data } = useClearingInvoices({
    makerOrganizationUuid,
    takerMarkets,
    options: {
      sortBy: sort.key,
      sortDirection: sort.direction === 'asc' ? 'ASC' : 'DESC',
      ...(currentCursor && { cursor: currentCursor }),
      limit: urlState.pageSize,
    },
    ...(!!search && { search }),
  });

  const nextCursor = data?.pageInfo?.nextCursor ?? null;

  useEffect(() => {
    setNextCursor(nextCursor);
  }, [nextCursor, setNextCursor]);

  const awardedInvoices = useMemo(
    () => mapClearingInvoicesToAwardedRows({ invoices: data?.invoices ?? [], takerMarkets }),
    [data?.invoices, takerMarkets]
  );

  return (
    <div className="overflow-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead isSorted={sort.key === 'voucherId'} onClick={() => handleSort('voucherId')}>
              <TableSortArrow accessorKey="voucherId" sort={sort}>
                {t('core.invoiceId')}
              </TableSortArrow>
            </TableHead>
            <TableHead isSorted={sort.key === 'amount'} onClick={() => handleSort('amount')}>
              <TableSortArrow accessorKey="amount" sort={sort} textRight>
                {t('core.invoiceAmount')}
              </TableSortArrow>
            </TableHead>
            <TableHead isSorted={sort.key === 'dueDate'} onClick={() => handleSort('dueDate')}>
              <TableSortArrow accessorKey="dueDate" sort={sort}>
                {hasIndiaDivisions ? t('core.tentativePaymentDate') : t('core.originalDueDate')}
              </TableSortArrow>
            </TableHead>
            {/* No SEA sorting for market clear date */}
            <TableHead>{t('core.marketClearDate')}</TableHead>
            <TableHead isSorted={sort.key === 'newPayDate'} onClick={() => handleSort('newPayDate')}>
              <TableSortArrow accessorKey="newPayDate" sort={sort}>
                {t('core.newPayDate')}
              </TableSortArrow>
            </TableHead>
            <TableHead isSorted={sort.key === 'minDiscount'} onClick={() => handleSort('minDiscount')}>
              <TableSortArrow accessorKey="minDiscount" sort={sort} textRight>
                {t('core.discountWithPercent')}
              </TableSortArrow>
            </TableHead>
            <TableHead isSorted={sort.key === 'earn'} onClick={() => handleSort('earn')}>
              <TableSortArrow accessorKey="earn" sort={sort} textRight>
                {t('core.discount')}
              </TableSortArrow>
            </TableHead>
            <TableHead isSorted={sort.key === 'clearingStatus'} onClick={() => handleSort('clearingStatus')}>
              <TableSortArrow accessorKey="clearingStatus" sort={sort} textRight>
                {t('core.status')}
              </TableSortArrow>
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {awardedInvoices.length > 0 ? (
            awardedInvoices.map((awardedInvoice) => {
              const statusConfig = statusChipConfig[awardedInvoice.clearingStatus];

              return (
                <TableRow key={awardedInvoice.voucherId}>
                  <TableCell>
                    <div className="max-w-80 whitespace-pre-wrap break-words">{awardedInvoice.voucherId}</div>
                  </TableCell>
                  <TableCell className="text-right">
                    {asCurrency(awardedInvoice.amount, awardedInvoice.currency)}
                  </TableCell>
                  <TableCell>{awardedInvoice.dueDate && asDateStringText({ date: awardedInvoice.dueDate })}</TableCell>
                  <TableCell>
                    {awardedInvoice.marketClearDate && asDateStringText({ date: awardedInvoice.marketClearDate })}
                  </TableCell>
                  <TableCell>
                    {awardedInvoice.newPayDate && asDateStringText({ date: awardedInvoice.newPayDate })}
                  </TableCell>
                  <TableCell className="text-right">
                    {asPercent({
                      value: awardedInvoice.minDiscount / 100,
                      decimals: 2,
                    })}
                  </TableCell>
                  <TableCell className="text-right">
                    {asCurrency(awardedInvoice.earn, awardedInvoice.currency)}
                  </TableCell>
                  <TableCell className="flex justify-end">
                    <Popover>
                      <Popover.Trigger aria-label="Awarded status information">
                        <Chip label={t(statusConfig.labelKey)} type={statusConfig.type} useHoverStyles />
                      </Popover.Trigger>
                      <Popover.Content arrow collisionPadding={24}>
                        <div className="w-72 space-y-2 text-center text-sm">
                          <div>{t(statusConfig.tooltipKey)}</div>
                        </div>
                      </Popover.Content>
                    </Popover>
                  </TableCell>
                </TableRow>
              );
            })
          ) : (
            <TableDisplay colSpan={8}>
              <NoDataState
                className="py-8"
                icon={<img src={invoices} alt="invoices" className="h-28 w-28" />}
                title={t('invoices.noAvailableInvoices')}
                message={t('invoices.noAvailableAwardedInvoicesDescription')}
              />
            </TableDisplay>
          )}
        </TableBody>
      </Table>
    </div>
  );
};
